<script setup lang="ts">
import { v4 as uuid } from "uuid";
import { computed, onBeforeMount, ref } from "vue";
import { useI18n } from "vue-i18n";

import type { Art } from "@/model/session";
import { useArtStore } from "@/store/art";
import { useTeamStore } from "@/store/team";

import TreeView from "./internal/TreeView.vue";
import type { TreeNodeData } from "./internal/types";
import type { OrganizationTreeNodeData, SelectedNode, SelectionMode } from "./types";

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    selected: SelectedNode;
    mode?: SelectionMode;
    checkedIds?: Set<string>;
    initialCheckedTeamIds?: string[];
  }>(),
  {
    mode: "single",
    checkedIds: () => new Set<string>(),
    initialCheckedTeamIds: () => [],
  },
);

const emit = defineEmits<{
  select: [node: OrganizationTreeNodeData];
  "update:checkedIds": [ids: Set<string>];
}>();

const treeId = ref<string>();
onBeforeMount(() => {
  treeId.value = uuid();

  if (props.initialCheckedTeamIds.length > 0) {
    const nodeIds = new Set<string>(
      props.initialCheckedTeamIds.map((teamId) => `${treeId.value}-team-${teamId}`),
    );
    emit("update:checkedIds", nodeIds);
  }
});

const arts = computed(() => useArtStore().arts);

const getTeamsInArt = (artId: string) => useTeamStore().teamsInArt(artId);

const showCheckboxes = computed(() => props.mode === "multi");

const handleSelect = (node: TreeNodeData) => {
  emit("select", node as OrganizationTreeNodeData);
};

// Helper functions for multi-select cascading logic
function getAllDescendantIds(node: OrganizationTreeNodeData): string[] {
  const ids: string[] = [node.id];
  for (const child of node.children) {
    ids.push(...getAllDescendantIds(child as OrganizationTreeNodeData));
  }
  return ids;
}

function areAllChildrenChecked(node: OrganizationTreeNodeData, checkedIds: Set<string>): boolean {
  if (node.children.length === 0) return false;
  return node.children.every((child) => checkedIds.has(child.id));
}

function areSomeChildrenChecked(node: OrganizationTreeNodeData, checkedIds: Set<string>): boolean {
  if (node.children.length === 0) return false;
  return node.children.some(
    (child) => checkedIds.has(child.id) || (child as OrganizationTreeNodeData).indeterminate,
  );
}

function calculateCheckState(
  node: OrganizationTreeNodeData,
  checkedIds: Set<string>,
): { checked: boolean; indeterminate: boolean } {
  const hasChildren = node.children.length > 0;

  if (!hasChildren) {
    return {
      checked: checkedIds.has(node.id),
      indeterminate: false,
    };
  }

  // For parent nodes, check children state
  const allChildrenChecked = areAllChildrenChecked(node, checkedIds);
  const someChildrenChecked = areSomeChildrenChecked(node, checkedIds);

  return {
    checked: allChildrenChecked,
    indeterminate: !allChildrenChecked && someChildrenChecked,
  };
}

function applyCheckState(
  nodes: OrganizationTreeNodeData[],
  checkedIds: Set<string>,
): OrganizationTreeNodeData[] {
  return nodes.map((node) => {
    const { checked, indeterminate } = calculateCheckState(node, checkedIds);
    return {
      ...node,
      checked,
      indeterminate,
      children: applyCheckState(node.children as OrganizationTreeNodeData[], checkedIds),
    };
  });
}

const handleCheckboxToggle = (node: TreeNodeData, checked: boolean) => {
  const orgNode = node as OrganizationTreeNodeData;
  const newCheckedIds = new Set(props.checkedIds);

  if (checked) {
    // Add node and all descendants
    const descendantIds = getAllDescendantIds(orgNode);
    descendantIds.forEach((id) => newCheckedIds.add(id));
  } else {
    // Remove node and all descendants
    const descendantIds = getAllDescendantIds(orgNode);
    descendantIds.forEach((id) => newCheckedIds.delete(id));
  }

  emit("update:checkedIds", newCheckedIds);
};

function createTeamNodes(artId: string): OrganizationTreeNodeData[] {
  return getTeamsInArt(artId).map((team) => ({
    id: `${treeId.value}-team-${team.id}`,
    label: team.name,
    icon: "persona/team",
    disabled: false,
    selected: props.selected.level === "team" && props.selected.id === team.id,
    levelContext: { level: "team", id: team.id },
    children: [],
  }));
}

function createArtNode(art: Art): OrganizationTreeNodeData {
  return {
    id: `${treeId.value}-art-${art.id}`,
    label: art.name,
    icon: "general/art",
    disabled: false,
    selected: props.selected.level === "art" && props.selected.id === art.id,
    levelContext: { level: "art", id: art.id },
    children: createTeamNodes(art.id),
  };
}

function createSolutionTrainNode(): OrganizationTreeNodeData {
  return {
    id: `${treeId.value}-solution-train`,
    label: t("treeView.solutionTrain"),
    icon: "general/solution-train",
    disabled: false,
    selected: props.selected.level === "solution-train",
    levelContext: { level: "solution-train" },
    children: arts.value.map(createArtNode),
  };
}

const nodes = computed<OrganizationTreeNodeData[]>(() => {
  let baseNodes: OrganizationTreeNodeData[];

  if (arts.value.length > 1) {
    baseNodes = [createSolutionTrainNode()];
  } else {
    // No ART or Single ART
    const isNoArt = useArtStore().current.id === "";
    const art = isNoArt
      ? { id: "", name: t("treeView.art") }
      : { id: arts.value[0].id, name: arts.value[0].name };

    baseNodes = [createArtNode(art)];
  }

  // Apply check state for multi-select mode
  if (props.mode === "multi" && props.checkedIds) {
    return applyCheckState(baseNodes, props.checkedIds);
  }

  return baseNodes;
});
</script>

<template>
  <div class="tree-view-container">
    <TreeView
      :nodes
      :show-checkboxes
      :expanded-by-default="mode === 'multi'"
      @select="handleSelect"
      @toggle-checkbox="handleCheckboxToggle"
    />
  </div>
</template>

<style lang="scss" scoped>
.tree-view-container {
  outline: revert;
}
</style>
